<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import MetaConnectionAPI from 'dashboard/api/metaConnection';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const router = useRouter();
const loading = ref(true);
const saving = ref(false);
const pageId = ref('');
const status = ref({});

const fb = computed(() => status.value.facebook || { connected: false });
const ig = computed(() => status.value.instagram || { connected: false });
const anyConnected = computed(() => fb.value.connected || ig.value.connected);

const loadStatus = async () => {
  try {
    const { data } = await MetaConnectionAPI.getStatus();
    status.value = data || {};
  } catch (e) {
    status.value = {};
  }
};

onMounted(async () => {
  loading.value = true;
  await loadStatus();
  loading.value = false;
});

const connect = async () => {
  if (!pageId.value.trim()) {
    useAlert('Informe o ID da Página do Facebook.');
    return;
  }
  saving.value = true;
  try {
    const { data } = await MetaConnectionAPI.connect(pageId.value.trim());
    status.value = data;
    pageId.value = '';
    const now = data.connected_now || {};
    const parts = [];
    if (now.facebook?.connected) parts.push(`Facebook (${now.facebook.page_name || 'página'})`);
    if (now.instagram?.connected) parts.push(`Instagram (@${now.instagram.username || ''})`);
    useAlert(parts.length ? `Vinculado: ${parts.join(' e ')}.` : 'Vínculo atualizado.');
    // avisos do que NÃO conectou (ex.: página sem Instagram vinculado)
    if (now.instagram && !now.instagram.connected && now.instagram.error) {
      useAlert(now.instagram.error);
    }
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Não foi possível vincular a Página. Confira o ID.');
  } finally {
    saving.value = false;
  }
};

const disconnect = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm('Desvincular a Página? As automações de Instagram e Facebook ficam inativas.')) return;
  try {
    await MetaConnectionAPI.disconnect();
    await loadStatus();
    useAlert('Página desvinculada.');
  } catch (e) {
    useAlert('Não foi possível desvincular.');
  }
};

const copy = (text, msg) => {
  copyTextToClipboard(text);
  useAlert(msg);
};

const goIg = () => router.push({ name: 'instagram_automation_index' });
const goFb = () => router.push({ name: 'facebook_automation_index' });
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto">
    <div class="w-full max-w-[60rem] mx-auto p-6 flex flex-col gap-6">
      <header class="flex flex-col gap-1">
        <h1 class="text-xl font-extrabold text-n-slate-12">Meta — Instagram & Facebook</h1>
        <p class="text-sm text-n-slate-11">
          Vincule a sua <strong>Página do Facebook</strong> uma única vez. O sistema habilita as
          automações de comentário do <strong>Facebook</strong> e do <strong>Instagram</strong>
          (a conta de Instagram Business vinculada à Página) — sem colar token.
        </p>
      </header>

      <div v-if="loading" class="flex justify-center py-10 text-n-slate-11"><Spinner /></div>

      <template v-else>
        <section class="flex flex-col gap-4 p-4 rounded-2xl border border-n-weak bg-n-alpha-1">
          <h2 class="text-sm font-semibold text-n-slate-12">Conexão</h2>

          <!-- Status por canal -->
          <div class="flex flex-col gap-2">
            <div class="flex items-center gap-2 text-sm">
              <span :class="[fb.connected ? 'i-ri-checkbox-circle-line text-n-teal-11' : 'i-ri-checkbox-blank-circle-line text-n-slate-10', 'size-4']" />
              <span :class="fb.connected ? 'text-n-slate-12' : 'text-n-slate-10'">
                <strong>Facebook</strong>
                <template v-if="fb.connected"> — {{ fb.page_name || fb.page_id }}</template>
                <template v-else> — não vinculado</template>
              </span>
            </div>
            <div class="flex items-center gap-2 text-sm">
              <span :class="[ig.connected ? 'i-ri-checkbox-circle-line text-n-teal-11' : 'i-ri-checkbox-blank-circle-line text-n-slate-10', 'size-4']" />
              <span :class="ig.connected ? 'text-n-slate-12' : 'text-n-slate-10'">
                <strong>Instagram</strong>
                <template v-if="ig.connected"> — @{{ ig.username }}</template>
                <template v-else> — não vinculado</template>
              </span>
            </div>
          </div>

          <!-- Form de vínculo -->
          <div class="flex flex-col gap-2 pt-1">
            <Input
              v-model="pageId"
              :label="anyConnected ? 'Vincular outra Página (substitui a atual)' : 'ID da Página do Facebook'"
              placeholder="Ex.: 102938475610293"
            />
            <p class="text-xs text-n-slate-10">
              O ID fica em <em>Configurações da Página → Sobre → ID da Página</em>. A Página precisa
              estar no Business da SiteUP.
            </p>
            <div class="flex gap-2">
              <Button :label="anyConnected ? 'Revincular' : 'Vincular Página'" size="sm" :is-loading="saving" @click="connect" />
              <Button v-if="anyConnected" label="Desvincular" variant="faded" color="ruby" size="sm" icon="i-ri-plug-line" @click="disconnect" />
            </div>
          </div>

          <!-- Configuração do app Meta (instância): os 3 webhooks do MESMO app +
               secret/verify/token compartilhados. Sempre visível como referência única
               (Formulários Meta, comentários FB e IG usam tudo isto — configura 1×). -->
          <div class="flex flex-col gap-2 p-3 rounded-xl bg-n-alpha-2 border border-n-weak text-xs text-n-slate-11">
            <span class="flex items-start gap-1.5 text-n-slate-12">
              <span class="i-ri-information-line size-4 shrink-0 mt-0.5" />
              <span>
                <strong>Configuração do app Meta (uma vez).</strong> O mesmo app vale
                pros 3 recursos — inscreva cada webhook no campo indicado.
                <code>META_LEADGEN_APP_SECRET</code>, o verify token e o System User
                token (<code>META_LEADGEN_TOKEN</code>) são os mesmos pros três.
              </span>
            </span>
            <div class="flex flex-col gap-1">
              <span>Objeto <code>page</code> — campos <code>leadgen</code> + <code>feed</code>
                (Formulários e comentários do Facebook usam a MESMA callback; o painel do
                Meta só aceita uma URL por objeto):
                <code class="break-all">{{ status.page_webhook_url || status.leadgen_webhook_url }}</code>
                <Button label="copiar" variant="link" size="xs" @click="copy(status.page_webhook_url || status.leadgen_webhook_url, 'Webhook do objeto page copiado.')" />
              </span>
              <span>Objeto <code>instagram</code> — campo <code>comments</code>:
                <code class="break-all">{{ status.ig_webhook_url }}</code>
                <Button label="copiar" variant="link" size="xs" @click="copy(status.ig_webhook_url, 'Webhook do Instagram copiado.')" />
              </span>
              <span v-if="status.verify_token">Verify token (o mesmo pros dois objetos):
                <code class="break-all">{{ status.verify_token }}</code>
                <Button label="copiar" variant="link" size="xs" @click="copy(status.verify_token, 'Verify token copiado.')" />
              </span>
            </div>
          </div>
        </section>

        <!-- Atalhos pras automações -->
        <section v-if="anyConnected" class="flex flex-col gap-2">
          <h2 class="text-sm font-semibold text-n-slate-12">Automações</h2>
          <p class="text-xs text-n-slate-11">Configure as palavras-chave e os posts em cada canal:</p>
          <div class="flex gap-2">
            <Button v-if="ig.connected" label="Comentário Instagram → DM" variant="faded" color="slate" size="sm" icon="i-ri-instagram-line" @click="goIg" />
            <Button v-if="fb.connected" label="Comentário Facebook → DM" variant="faded" color="slate" size="sm" icon="i-ri-facebook-line" @click="goFb" />
          </div>
        </section>
      </template>
    </div>
  </div>
</template>
